import { PaymentService } from './../../api/services/payment.service';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { Observable, finalize, forkJoin } from 'rxjs';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";

import { AnalyticsService, SalesAnalytics, InventoryAlerts } from '../../api/services/analytics.service';
import { StaffService } from '../../api/services/staff.service';
import { QrCodeService, QrCodeStatistics } from '../../api/services/qr-code.service';
import { OrderService } from '../../api/services/order.service';
import { DrinksService } from '../../api/services/drinks.service';
import { AuthService } from '../../api/services/auth.service';
import { ServiceView } from '../../models';
import { Staff } from 'src/app/api/models';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { StatPayment } from 'src/app/api/services/payment.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-service-provider-app',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatToolbarModule, MatGridListModule, MatBadgeModule, MatChipsModule, HeaderNavComponent, FooterComponent, MatProgressSpinner],
  templateUrl: './service-provider-app.component.html',
  styles: []
})
export class ServiceProviderAppComponent implements OnInit {
  currentView: ServiceView = 'dashboard';

  //Spinner loading
  isLoading = true;

  // Dashboard Stats
  todaysOrders = 0;
  todaysRevenue = 0;
  activeStaffCount = 0;
  lowStockCount = 0;
  
  // Inventory Stats
  totalDrinks = 0;
  
  // Order Stats
  pendingOrdersCount = 0;
  completedOrdersToday = 0;
  
  // Payment Stats
  todaysTransactions = 0;
  todaysPayments = 0;
  stats: any = {
    totalRevenue: 0,
    totalTips: 0,
    totalTransactions: 0,
    pendingCount: 0,
    statusCounts: {}
  };
  
  // Staff Stats
  totalStaff = 0;
  
  // QR Stats
  totalQrCodes = 0;
  todaysQrScans = 0;

  recentActivity: any[] = [];
  dashboardSummary: any = null;
  bars: any[] = [];

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private staffService: StaffService,
    private qrCodeService: QrCodeService,
    private orderService: OrderService,
    private drinksService: DrinksService,
    private paymentService: PaymentService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
    this.loadDashboardSummary();
    this.loadBars();
  }

  loadDashboardSummary(): void {
    const today = new Date().toISOString().split('T')[0];
    this.analyticsService.getDashboardSummary(today).subscribe({
      next: (summary) => this.dashboardSummary = summary,
      error: () => {}
    });
  }

  loadBars(): void {
    this.authService.getBars().subscribe({
      next: (bars) => this.bars = bars,
      error: () => {}
    });
  }

  loadDashboardData(): void {
    this.isLoading = true;
    // Load Analytics
    this.analyticsService.getSalesAnalytics().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (data: any) => {
        this.todaysOrders = data.totalOrders?.content?.length; // This might be total for the range, need to verify API behavior for "today"
        //this.todaysRevenue = data.totalSales;
      },
      error: (err) => console.error('Error loading sales analytics:', err)
    });

    /** INVENTORY MANAGER SERVICE */
    /** ************************** */
    // Load Inventory Alerts (Low Stock)
    this.drinksService.getLowStockDrinks().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (drinks) => {
        this.lowStockCount = drinks.length;
      },
      error: (err) => console.error('Error loading low stock drinks:', err)
    });
    // Load Drinks Count
    this.drinksService.getDrinks().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (drinks) => {
        this.totalDrinks = drinks.length;
      },
      error: (err) => console.error('Error loading drinks:', err)
    });

    /** STAFF MANAGER SERVICE */
    /** ********************* */
    // Load Staff Data
    this.staffService.getStaffList().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (staff: any) => {
        this.totalStaff = staff.length;
      },
      error: (err) => console.error('Error loading staff:', err)
    });
    // Load Active Staff
    this.staffService.getActiveStaff().subscribe({
      next: (staff: Staff[]) => {
        this.activeStaffCount = staff.length;
      },
      error: (err) => console.error('Error loading active staff:', err)
    });

    /** PAYMENT SERVICE */
    /** *************** */
    this.loadPaymentStats();

    /** QR CODE SERVICE */
    /** *************** */
    // Load QR Codes
    this.qrCodeService.getStatistics().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (stats: QrCodeStatistics) => {
        this.totalQrCodes = stats.totalQRCodes;
        this.todaysQrScans = stats.totalScanCount;
      },
      error: (err) => console.error('Error loading QR codes:', err)
    });

    /** ORDER SERVICE */
    /** *************** */
    this.loadOrders();
    // Load Orders for Pending Count
    // Note: Ideally we should have an endpoint for counts to avoid fetching all orders
    // this.orderService.getOrdersByStatus('PENDING').pipe(finalize(() => this.isLoading = false))
    // .subscribe({
    //   next: (orders: any) => {
    //     this.pendingOrdersCount = orders.length; // Assuming getOrders returns array
    //   },
    //   error: (err) => console.error('Error loading pending orders:', err)
    // });
    // // Load Orders for Completed Count
    // this.orderService.getOrders().pipe(finalize(() => this.isLoading = false))
    // .subscribe({
    //   next: (orders: any) => { //get all orders in content property and filter by completed property true
    //     this.completedOrdersToday = orders.content.filter((order: any) => order.completed === true).length;
    //   },
    //   error: (err) => console.error('Error loading completed orders:', err)
    // });


    /** RECENT ACTIVITY */
    /** *************** */
    this.loadRecentActivityData();
    
  }

  /**
   * Load recent activity data from backend
   */
  loadRecentActivityData(): void {
    forkJoin({
      orders: this.orderService.getOrders(0, 1),
      payments: this.paymentService.getPayments(0, 1, 'COMPLETED'),
      staff: this.staffService.getStaffList(),
      lowStock: this.drinksService.getLowStockDrinks()
    }).subscribe({
      next: (data) => {
        const activities: any[] = [];

        // Latest order
        if (data.orders.content && data.orders.content.length > 0) {
          const latestOrder = data.orders.content[0];
          const orderDescription = latestOrder.items 
            ? latestOrder.items.slice(0, 2).map((item: any) => item.drink?.name || 'Item').join(', ')
            : 'New order';
          
          activities.push({
            title: `New order ${latestOrder.invoiceNumber || '#' + latestOrder.id}${latestOrder.tableNumber ? ' from Table ' + latestOrder.tableNumber : ''}`,
            description: `${orderDescription} - $${latestOrder.total?.toFixed(2) || '0.00'}`,
            time: latestOrder.createdAt ? this.getTimeAgo(latestOrder.createdAt) : 'Recently',
            colorClass: 'bg-green-500'
          });
        }

        // Latest completed payment
        if (data.payments && data.payments.length > 0) {
          const latestPayment = data.payments[0];
          activities.push({
            title: 'Payment completed',
            description: `${latestPayment.invoiceNumber || 'Invoice'} - $${latestPayment.amount?.toFixed(2) || '0.00'} (${latestPayment.paymentMethod || 'Card'})`,
            time: latestPayment.createdAt ? this.getTimeAgo(latestPayment.createdAt) : 'Recently',
            colorClass: 'bg-blue-500'
          });
        }

        // Low stock alert
        if (data.lowStock && data.lowStock.length > 0) {
          const lowestStock = data.lowStock[0];
          activities.push({
            title: 'Low stock alert',
            description: `${lowestStock.name} - Only ${lowestStock.stock || 0} left`,
            time: 'Current',
            colorClass: 'bg-orange-500'
          });
        }

        // Most recently active staff
        if (data.staff && data.staff.length > 0) {
          const recentStaff = data.staff
            .filter(s => s.lastActive)
            .sort((a, b) => new Date(b.lastActive!).getTime() - new Date(a.lastActive!).getTime())[0];
          
          if (recentStaff && recentStaff.lastActive) {
            activities.push({
              title: 'Staff check-in',
              description: `${recentStaff.name} ${recentStaff.role ? '(' + recentStaff.role + ')' : 'started shift'}`,
              time: this.getTimeAgo(recentStaff.lastActive),
              colorClass: 'bg-purple-500'
            });
          }
        }

        this.recentActivity = activities;
        if (!environment.production) {
          console.log('✅ Recent activity loaded:', activities.length, 'items');
        }
      },
      error: (error) => {
        if (!environment.production) {
          console.error('❌ Error loading recent activity:', error);
        }
        // Fallback to empty array
        this.recentActivity = [];
      }
    });
  }

  /**
   * Convert timestamp to relative time (e.g., "2 mins ago")
   */
  private getTimeAgo(timestamp: string | Date): string {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return time.toLocaleDateString();
  }

  onBack(): void {
    this.router.navigate(['/']);
  }

  
  /** PAYMENT SERVICE */
  /** *************** */
  loadPaymentStats(): void {
    const today = new Date();
    // Start of today (00:00:00)
    const startObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    // Current time
    const endObj = new Date();

    const formatDate = (date: Date) => {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const start = formatDate(startObj);
    const end = formatDate(endObj);
    
    this.paymentService.getStatistics(start, end).subscribe(stats => {
      this.todaysTransactions = stats.totalPayments || 0;
      this.todaysPayments = stats.totalRevenue || 0;
      this.todaysRevenue = stats.totalRevenue || 0;
    });
  }

  /** ORDER SERVICE */
  /** *************** */
  loadOrders(): void {
    const today = new Date();
    // Start of today (00:00:00)
    const startObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    // Current time
    const endObj = new Date();

    const formatDate = (date: Date) => {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const start = formatDate(startObj);
    const end = formatDate(endObj);
    
    this.orderService.getStatistics(start, end).subscribe(stats => {
      this.todaysOrders = stats.ordersInPeriod;
    });

    this.orderService.getOrdersByStatus('PENDING').pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (orders: any) => {
        this.pendingOrdersCount = orders.length; // Assuming getOrders returns array
      },
      error: (err) => console.error('Error loading pending orders:', err)
    });
    // Load Orders for Completed Count
    this.orderService.getOrders().pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (orders: any) => { //get all orders in content property and filter by completed property true
        this.completedOrdersToday = orders.content.filter((order: any) => order.completed === true).length;
      },
      error: (err) => console.error('Error loading completed orders:', err)
    });
  }

  navigateToView(view: ServiceView): void {
    switch(view) {
      case 'inventory':
        this.router.navigate(['/service-provider/inventory']);
        break;
      case 'orders':
        this.router.navigate(['/service-provider/orders']);
        break;
      case 'payments':
        this.router.navigate(['/service-provider/payments']);
        break;
      case 'staff':
        this.router.navigate(['/service-provider/staff']);
        break;
      case 'qr-codes':
        this.router.navigate(['/service-provider/qr-codes']);
        break;
      case 'analytics':
        this.router.navigate(['/service-provider/analytics']);
        break;
      case 'chat':
        this.router.navigate(['/service-provider/chat']);
        break;
    }
  }
}